import { useEffect, useState } from "react";
import api from "../../../../services/api";
import { buildPayload } from "./reviewUtils";
import { validateForPricing } from "./utils/validateForPricing";

export function useReviewPreview(state, { enabled, deps = [] } = {}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState([]);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState(null);
  const [warnings, setWarnings] = useState([]);

  // NEW: structured pricing payload for the Breakdown card
  const [pricing, setPricing] = useState({
    lines: [],
    rollup: null,
    cakeDetail: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setError("");
      setErrorDetails([]);
      setWarnings([]);
      setTotals(null);
      setItems([]);
      setPricing({ lines: [], rollup: null, cakeDetail: null });

      if (!enabled) return;

      // pre-validate to avoid noisy 400s and give instant UX
      const { ok, errs } = validateForPricing(state);
      if (!ok) {
        if (!cancelled) {
          setError(
            "Please complete contact info, date, and address (if delivering/setting up)."
          );
          setErrorDetails(errs);
        }
        return;
      }

      setBusy(true);
      try {
        const payload = buildPayload(state, { preview: true });
        const { data } = await api.post("/weddings/cart-items", payload);
        if (cancelled) return;

        const okFlag = data?.ok === true;
        const serverItems = Array.isArray(data?.items) ? data.items : [];
        const serverTotals = data?.totals || null;
        const serverWarnings = Array.isArray(data?.warnings)
          ? data.warnings
          : [];
        const serverErr = data?.error;

        setItems(serverItems);
        setTotals(serverTotals);
        setWarnings(serverWarnings);

        // NEW: extract pricing details (lines/rollup) and cake breakdown detail
        const p = data?.previewPricing?._pricing;
        const cakeDetail =
          data?.summary?.cake?.breakdown?.detail ||
          data?.previewPricing?.cakeDetail || // in case you expose directly later
          null;

        setPricing({
          lines: Array.isArray(p?.lines) ? p.lines : [],
          rollup: p?.rollup || null,
          cakeDetail,
        });

        if (!okFlag && serverErr) {
          setError(serverErr.message || "Could not compute preview.");
          setErrorDetails(
            Array.isArray(serverErr.details) ? serverErr.details : []
          );
        } else {
          setError("");
          setErrorDetails([]);
        }
      } catch (e) {
        if (!cancelled) {
          const err = e?.response?.data?.error;
          setError(err?.message || "Could not compute preview.");
          setErrorDetails(Array.isArray(err?.details) ? err.details : []);
        }
        console.error("preview /weddings/cart-items failed", e);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // return the new pricing object alongside existing fields
  return { busy, error, errorDetails, items, totals, warnings, pricing };
}
